import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtDateTime, ruleConfigSchema, type EmployeeDTO, type RuleAssignmentDTO } from '@em/shared';
import { api, qk, queryClient, useEmployee, useRuleTemplates, useSettings, useSites } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { titleCase, todayIn } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, PageHeader, QueryState, Select, Tabs, Textarea } from '../../components/ui';
import { EmployeeForm } from './EmployeeForm';

type Tab = 'profile' | 'rules' | 'devices' | 'activity';

export function EmployeeDetailPage() {
  const { id = '' } = useParams();
  const q = useEmployee(id);
  return <QueryState q={q} loadingLabel="Loading employee">{(e) => <EmployeeView e={e} />}</QueryState>;
}

function EmployeeView({ e }: { e: EmployeeDTO }) {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('profile');
  const [editing, setEditing] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const admin = can('payroll_admin');

  return (
    <>
      <PageHeader
        breadcrumb={<Link to="/employees">Employees</Link>}
        title={
          <span className="row">
            {e.displayName}
            <Badge tone={e.status === 'active' ? 'green' : e.status === 'suspended' ? 'amber' : 'grey'}>{titleCase(e.status)}</Badge>
          </span>
        }
        subtitle={`Employee ${e.employeeNumber}${e.teamName ? ` · ${e.teamName}` : ''}`}
        actions={
          admin && (
            <>
              <Button onClick={() => setPinOpen(true)}>{e.hasPin ? 'Change PIN' : 'Set PIN'}</Button>
              <Button variant="primary" onClick={() => setEditing(true)}>
                Edit profile
              </Button>
            </>
          )
        }
      />
      <Tabs
        label="Employee sections"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'profile', label: 'Profile' },
          { value: 'rules', label: 'Pay rules' },
          { value: 'devices', label: 'Devices' },
          { value: 'activity', label: 'Activity' },
        ]}
      />
      {tab === 'profile' && <Profile e={e} />}
      {tab === 'rules' && <RuleAssignments e={e} />}
      {tab === 'devices' && <Devices e={e} />}
      {tab === 'activity' && <Timeline e={e} />}

      {editing && <EmployeeForm employee={e} onClose={() => setEditing(false)} />}
      {pinOpen && <PinDialog e={e} onClose={() => setPinOpen(false)} />}
    </>
  );
}

function Profile({ e }: { e: EmployeeDTO }) {
  const sites = useSites();
  return (
    <Card>
      <dl className="kv">
        <dt>Legal name</dt>
        <dd>
          {e.firstName} {e.lastName}
          {e.preferredName ? ` (prefers ${e.preferredName})` : ''}
        </dd>
        <dt>Employment</dt>
        <dd>{titleCase(e.employmentType)}</dd>
        <dt>Started</dt>
        <dd>{fmtDate(e.startDate)}</dd>
        <dt>Ends</dt>
        <dd>{e.endDate ? fmtDate(e.endDate) : '–'}</dd>
        <dt>Email</dt>
        <dd>{e.email ?? '–'}</dd>
        <dt>Mobile</dt>
        <dd>{e.mobile ?? '–'}</dd>
        <dt>Team</dt>
        <dd>{e.teamName ?? '–'}</dd>
        <dt>Primary site</dt>
        <dd>{sites.data?.find((s) => s.id === e.primarySiteId)?.name ?? '–'}</dd>
        <dt>MYOB card ID</dt>
        <dd>{e.myobCardId ?? <span className="amber">Not set</span>}</dd>
        <dt>MYOB employee</dt>
        <dd className="mono">{e.myobUid ?? <span className="amber">Not matched</span>}</dd>
        <dt>PIN</dt>
        <dd>{e.hasPin ? 'Set' : <span className="amber">Not set</span>} · used at the kiosk and to sign in to the phone app</dd>
        <dt>Kiosk</dt>
        <dd>{e.kioskEnabled ? 'Enabled' : 'Disabled'}</dd>
        <dt>Phone app</dt>
        <dd>{e.mobile && e.hasPin ? `Signs in with ${e.mobile} and their PIN at ${location.origin}/app/` : <span className="amber">Needs a mobile number and a PIN</span>}</dd>
        <dt>Privacy notice</dt>
        <dd>{e.privacyAckVersion ? `Acknowledged version ${e.privacyAckVersion}` : <span className="amber">Not acknowledged</span>}</dd>
      </dl>
    </Card>
  );
}

function PinDialog({ e, onClose }: { e: EmployeeDTO; onClose: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setEmployeePin(e.id, pin);
      await queryClient.invalidateQueries({ queryKey: qk.employee(e.id) });
      toast.success('PIN set', 'Tell the employee their PIN in person; it is not shown again. They use it at the kiosk and in the phone app.');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`PIN for ${e.displayName}`}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!/^\d{4,6}$/.test(pin)}>
            Set PIN
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <Field label="New PIN" hint="4 to 6 digits, not a run like 1234 or a repeat like 1111, and not used by anyone else.">
          <Input className="code-input" inputMode="numeric" autoComplete="off" maxLength={6} value={pin} onChange={(ev) => setPin(ev.target.value.replace(/\D/g, ''))} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

function Devices({ e }: { e: EmployeeDTO }) {
  const { can } = useAuth();
  const q = useQuery({ queryKey: qk.employeeDevices(e.id), queryFn: () => api.employeeDevices(e.id) });
  const toast = useToast();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: qk.employeeDevices(e.id) });
      toast.success(msg);
    } catch (err) {
      toast.error(err);
    }
  };
  return (
    <Card tight>
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No phones yet">They sign in to the app at {location.origin}/app/ with their mobile number and PIN.</EmptyState>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Platform</th>
                  <th>App</th>
                  <th>Enrolled</th>
                  <th>Last seen</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <td className="strong">{d.name}</td>
                    <td>{d.platform ?? '–'}</td>
                    <td>{d.appVersion ?? '–'}</td>
                    <td className="nowrap">{fmtDateTime(d.enrolledAt, tz)}</td>
                    <td className="nowrap">{d.lastSeenAt ? fmtDateTime(d.lastSeenAt, tz) : '–'}</td>
                    <td>
                      <Badge tone={d.status === 'active' ? 'green' : d.status === 'pending' ? 'amber' : 'grey'}>{titleCase(d.status)}</Badge>
                    </td>
                    <td className="actions">
                      {can('payroll_admin') && d.status === 'pending' && (
                        <Button size="sm" variant="primary" onClick={() => act(() => api.approveDevice(d.id), 'Device approved')}>
                          Approve
                        </Button>
                      )}
                      {can('payroll_admin') && d.status !== 'revoked' && (
                        <Button size="sm" variant="danger" onClick={() => act(() => api.revokeDevice(d.id), 'Device revoked')}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
    </Card>
  );
}

function RuleAssignments({ e }: { e: EmployeeDTO }) {
  const { can } = useAuth();
  const admin = can('payroll_admin');
  const q = useQuery({ queryKey: qk.ruleAssignments(e.id), queryFn: () => api.ruleAssignments(e.id) });
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<RuleAssignmentDTO | null>(null);
  const [overriding, setOverriding] = useState<RuleAssignmentDTO | null>(null);
  return (
    <Card
      title="Rule assignments"
      tight
      actions={
        admin && (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            Assign rules
          </Button>
        )
      }
    >
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No pay rules assigned">Hours cannot be calculated until a rule template is assigned.</EmptyState>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Overrides</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id}>
                    <td className="strong">{a.templateName}</td>
                    <td className="nowrap">{fmtDate(a.effectiveFrom)}</td>
                    <td className="nowrap">{a.effectiveTo ? fmtDate(a.effectiveTo) : <Badge tone="green">Current</Badge>}</td>
                    <td className="small">{a.overrides && Object.keys(a.overrides).length ? Object.keys(a.overrides).join(', ') : <span className="muted">None</span>}</td>
                    <td className="actions">
                      {admin && (
                        <>
                          <Button size="sm" onClick={() => setOverriding(a)}>
                            Overrides
                          </Button>
                          {!a.effectiveTo && (
                            <Button size="sm" onClick={() => setEnding(a)}>
                              End
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
      {adding && <AssignDialog employeeId={e.id} onClose={() => setAdding(false)} />}
      {ending && <EndDialog a={ending} onClose={() => setEnding(null)} />}
      {overriding && <OverridesDialog a={overriding} onClose={() => setOverriding(null)} />}
    </Card>
  );
}

const invalidateRules = (employeeId: string) => {
  void queryClient.invalidateQueries({ queryKey: qk.ruleAssignments(employeeId) });
  for (const k of ['timesheet', 'pay-period', 'exceptions', 'rule-templates']) void queryClient.invalidateQueries({ queryKey: [k] });
};

/** Parse the overrides editor: empty = none; must be an object of known RuleConfig sections. */
function parseOverrides(text: string): { value: Record<string, unknown> | null; error: string | null } {
  if (!text.trim()) return { value: null, error: null };
  try {
    const v = JSON.parse(text);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { value: null, error: 'Overrides must be a JSON object' };
    const allowed = Object.keys(ruleConfigSchema.shape);
    const bad = Object.keys(v).filter((k) => !allowed.includes(k));
    if (bad.length) return { value: null, error: `Unknown rule settings: ${bad.join(', ')}. Allowed: ${allowed.join(', ')}` };
    return { value: v, error: null };
  } catch (err) {
    return { value: null, error: `Not valid JSON: ${(err as Error).message}` };
  }
}

function AssignDialog({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const templates = useRuleTemplates();
  const toast = useToast();
  const [templateId, setTemplateId] = useState('');
  const [from, setFrom] = useState(todayIn('Australia/Sydney'));
  const [to, setTo] = useState('');
  const [overrides, setOverrides] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseOverrides(overrides);
  const save = async () => {
    if (parsed.error) return;
    setBusy(true);
    setError(null);
    try {
      await api.createRuleAssignment(employeeId, { templateId, effectiveFrom: from, effectiveTo: to || null, overrides: parsed.value as never });
      invalidateRules(employeeId);
      toast.success('Rules assigned', 'Open timesheets from that date have been recalculated.');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Assign pay rules"
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!templateId || !from || !!parsed.error}>
            Assign
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <Field label="Rule template" required>
          <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)} required>
            <option value="">Choose a template</option>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="form-grid">
          <Field label="Effective from" required>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          </Field>
          <Field label="Effective to" hint="Leave empty for open-ended">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <OverridesEditor value={overrides} onChange={setOverrides} error={parsed.error} />
      </div>
    </Modal>
  );
}

function OverridesEditor({ value, onChange, error }: { value: string; onChange: (v: string) => void; error: string | null }) {
  return (
    <Field
      label="Overrides (JSON, optional)"
      error={error}
      hint={
        <>
          Only the settings that differ from the template, e.g. <code>{'{"minimumEngagementHours": 3}'}</code> or <code>{'{"grace": {"lateMinutes": 10}}'}</code>. Arrays replace the template's.
        </>
      }
    >
      <Textarea className="code" value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false} />
    </Field>
  );
}

function EndDialog({ a, onClose }: { a: RuleAssignmentDTO; onClose: () => void }) {
  const toast = useToast();
  const [to, setTo] = useState(todayIn('Australia/Sydney'));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.updateRuleAssignment(a.id, { effectiveTo: to });
      invalidateRules(a.employeeId);
      toast.success('Assignment ended');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`End ${a.templateName}`}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!to}>
            End assignment
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <p>To change templates, end this assignment the day before the new one starts.</p>
        <Field label="Last day" required>
          <Input type="date" value={to} min={a.effectiveFrom} onChange={(e) => setTo(e.target.value)} required />
        </Field>
      </div>
    </Modal>
  );
}

function OverridesDialog({ a, onClose }: { a: RuleAssignmentDTO; onClose: () => void }) {
  const toast = useToast();
  const [text, setText] = useState(a.overrides && Object.keys(a.overrides).length ? JSON.stringify(a.overrides, null, 2) : '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseOverrides(text);
  const save = async () => {
    if (parsed.error) return;
    setBusy(true);
    try {
      await api.updateRuleAssignment(a.id, { overrides: parsed.value as never });
      invalidateRules(a.employeeId);
      toast.success('Overrides saved', 'Open timesheets have been recalculated.');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Overrides: ${a.templateName}`}
      wide
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!!parsed.error}>
            Save overrides
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <OverridesEditor value={text} onChange={setText} error={parsed.error} />
      </div>
    </Modal>
  );
}

function Timeline({ e }: { e: EmployeeDTO }) {
  const q = useQuery({ queryKey: qk.employeeTimeline(e.id), queryFn: () => api.employeeTimeline(e.id) });
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card title="Activity" tight>
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No activity recorded" />
          ) : (
            <ul className="timeline" style={{ padding: '0 16px' }}>
              {list.map((a) => (
                <li key={a.id}>
                  <span className="small muted">{fmtDateTime(a.at, tz)}</span>
                  <div>
                    <strong>{a.action.replace(/[._]/g, ' ')}</strong> <span className="muted">by {a.actorName ?? a.actorType}</span>
                    {(a.before != null || a.after != null) && (
                      <>
                        {' '}
                        <Button size="sm" variant="ghost" onClick={() => setOpen(open === a.id ? null : a.id)} aria-expanded={open === a.id}>
                          {open === a.id ? 'Hide' : 'Details'}
                        </Button>
                        {open === a.id && (
                          <div className="grid grid-2">
                            <div>
                              <div className="small muted">Before</div>
                              <pre className="json">{JSON.stringify(a.before, null, 2) ?? 'null'}</pre>
                            </div>
                            <div>
                              <div className="small muted">After</div>
                              <pre className="json">{JSON.stringify(a.after, null, 2) ?? 'null'}</pre>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </Card>
  );
}
